import { Box, Button, FormField, Heading, List, Markdown, ResponsiveContext, Tab, Tabs, Text, TextInput } from "grommet";
import { debounce } from "lodash";
import React, { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { usePermission } from "../account/AccountStore";
import { AliasViewModel, searchAliases } from "../aliases/AliasStore";
import { AliasTile } from "../aliases/AliasTile";
import { addParticipant, removeParticipant } from "../seasons/SeasonsStore";
import { TeamRow } from "../teams/TeamsManagement";
import { getTeamMembers, searchTeams, TeamViewModel } from "../teams/TeamsStore";
import TournamentBlock from "../tournaments/TournamentBlock";
import { AddTournamentParticipantRequest, AliasTournamentParticipantViewModel, RemoveTournamentParticipantRequest, TournamentParticipantViewModel, updateParticipant, UpdateTournamentParticipantRequest } from "../tournaments/TournamentsStore";
import { CreateEntry, GetOrganizationInvite, OrganizationEntryAliasTeamParticipantViewModel, OrganizationEntryViewModel, OrganizationInviteViewModel, RemoveOrganizationEntry, UpdateOrganizationEntry } from "./RegistrationStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Organization } from "../organizations/OrganizationStore";
import CustomButton from "../components/CustomButton";
import { AliasRow } from "../aliases/AliasRow";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import OrganizationSearch from "../organizations/OrganizationSearch";
import TeamDetails from "../teams/TeamDetails";
import Avatar from "react-avatar";
import { format, parseJSON } from "date-fns";

const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;

const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
const addTeam = <FontAwesomeIcon icon={["fad", "user-plus"]}></FontAwesomeIcon>;

const OrganizationInviteView = () => {
    const { id } = useParams();
    const size = useContext(ResponsiveContext);
    const hasWritePermission = usePermission("sport:management");

    const [invite, setInvite] = React.useState({} as OrganizationInviteViewModel);

    const [isLoading, setIsLoading] = React.useState(true);

    const [isEntryLoading, setIsEntryLoading] = React.useState(false);
    const [teamToAdd, setTeamToAdd] = React.useState({} as TeamViewModel);
    const [entryAliasToAdd, setEntryAliasToAdd] = React.useState([] as AliasViewModel[]);

    const [showAddEntryModal, setShowAddEntryModal] = React.useState(false);
    const [addEntryError, setAddEntryError] = React.useState("");

    const [showRemoveEntryModal, setShowRemoveEntryModal] = React.useState(false);
    const [removeEntryError, setRemoveEntryError] = React.useState("");

    const [entryToRemove, setEntryToRemove] = React.useState({} as OrganizationEntryViewModel);

    const [organization, setOrganization] = React.useState({} as Organization);

    const [showAddAliasModal, setShowAddAliasModal] = React.useState(false);

    useEffect(() => {
        load();
    }, [id]);

    const load = () => {
        setIsLoading(true);
        GetOrganizationInvite(id).then((res) => {
            setInvite(res.result);
            setIsLoading(false);
        });
    };

    const startRemoveEntry = (entry: OrganizationEntryViewModel) => {
        setEntryToRemove(entry);
        setShowRemoveEntryModal(true);
    };

    const removeEntryFromTournament = () => {
        setRemoveEntryError("");
        setIsEntryLoading(true);
        RemoveOrganizationEntry(entryToRemove.id).then((response) => {
            setShowRemoveEntryModal(false);
            setIsEntryLoading(false);
            if (response.success) {
                load();
            } else {
                setRemoveEntryError(response.errors.join("\n"));
            }
        });
    };
    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);
    let [teamFilter, setTeamFilter] = React.useState("");

    const searchTeam = (filter, organizationID) => {
        searchTeams(filter, organizationID, 0, 10).then((response) => {
            setTeamSearchResult(response);
        });
    };

    const debounceSearchTeam = React.useMemo(() => debounce(searchTeam, 300), []);
    React.useEffect(() => {
        debounceSearchTeam(teamFilter, organization.id);
    }, [teamFilter, organization.id]);

    const selectTeamToAdd = (team: TeamViewModel) => {
        // setTeamToAdd(team);
        setIsEntryLoading(true);
        getTeamMembers(team.id).then((response) => {
            // setTeamAliasToAdd(response.data.result.map((r) => r.alias));

            CreateEntry({ aliasIDs: response.data.result.map((r) => r.alias.id), inviteID: invite.id, teamID: team.id }).then((response) => {
                load();
            });

            setIsEntryLoading(false);
            setShowAddEntryModal(false);
        });
    };

    const [entryToEdit, setEntryToEdit] = React.useState({} as OrganizationEntryViewModel);
    const [entryAliasToEdit, setEntryAliasToEdit] = React.useState([] as OrganizationEntryAliasTeamParticipantViewModel[]);
    const [showEntryEditModal, setShowEntryEditModal] = React.useState(false);

    const startEditParticipant = (entry: OrganizationEntryViewModel) => {
        setEntryToEdit(entry);
        setEntryAliasToEdit(entry.participatingAliases);
        setEntryAliasToAdd([]);
        setShowEntryEditModal(true);
    };

    let [userSearchResult, setUserSearchResult] = React.useState([] as Array<AliasViewModel>);

    const searchAlias = (filter, organizationID) => {
        searchAliases(filter, organizationID, 0, 10).then((response) => {
            setUserSearchResult(response);
        });
    };

    const debounceSearchAlias = React.useMemo(() => debounce(searchAlias, 300), []);

    let [userFilter, setUserFilter] = React.useState("");
    const [role, setRole] = React.useState("Member");

    const [memberCreateError, setMemberCreateError] = React.useState("");

    React.useEffect(() => {
        debounceSearchAlias(userFilter, organization.id);
    }, [userFilter, organization.id]);

    const updateParticipants = () => {
        return UpdateOrganizationEntry({
            aliasIDs: entryAliasToEdit.map((a) => a.aliasID).concat(entryAliasToAdd.map((a) => a.id)),
            organizationEntryID: entryToEdit.id,
            teamID: entryToEdit.team.id,
        });
    };

    const addPlayer = (alias: AliasViewModel) => {
        setMemberCreateError("");
        setEntryAliasToAdd([...entryAliasToAdd, alias]);
        setShowAddAliasModal(false);
    };

    return (
        <SkeletonWrapper loading={isLoading}>
            {invite.id && (
                <Box direction="row" gap="small">
                    <TournamentBlock tournament={invite.tournament}></TournamentBlock>
                    <Box fill>
                        <Heading level={3}>{invite.name}</Heading>
                        <Text>
                            Entries Close <Text color={"brand"}>{format(parseJSON(invite.endDate), "yyyy/MM/dd")}</Text>
                        </Text>
                        <Markdown>{invite.content}</Markdown>
                        <Box gap="small">
                            {invite.organizationEntries.map((entry) => (
                                <TeamRow team={entry.team} key={entry.id}>
                                    <Box direction={size == "small" ? "column" : "row"} gap="small">
                                        {entry.participatingAliases.map((aliasParticipant) => (
                                            <AliasTile key={aliasParticipant.id} alias={aliasParticipant.alias}></AliasTile>
                                        ))}
                                        {hasWritePermission && !invite.tournament.locked && new Date() < parseJSON(invite.endDate) && (
                                            <Box gap="small">
                                                <Button label="Manage Players" icon={edit} onClick={() => startEditParticipant(entry)} color="status-ok"></Button>
                                                <Button label="Remove Team" icon={xmark} onClick={() => startRemoveEntry(entry)} color="status-critical"></Button>
                                            </Box>
                                        )}
                                    </Box>
                                </TeamRow>
                            ))}
                        </Box>

                        {hasWritePermission && !invite.tournament.locked && new Date() < parseJSON(invite.endDate) && (
                            <Box direction="row" justify="end">
                                <CustomButton label="Add Team" icon={addTeam} onClick={() => setShowAddEntryModal(true)}></CustomButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            <StyledModal
                isOpen={showAddEntryModal}
                setIsOpen={(open) => {
                    setShowAddEntryModal(open);
                }}
            >
                <Box width={"medium"}>
                    <SkeletonWrapper loading={isEntryLoading}>
                        <OrganizationSearch onSelect={(organization) => setOrganization(organization)}></OrganizationSearch>
                        <FormField label="Search">
                            <TextInput size="xs" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} />
                        </FormField>
                        <List
                            pad="xs"
                            data={teamSearchResult}
                            itemKey="id"
                            primaryKey={(team: TeamViewModel) => (
                                <Text size="small" key={team.id}>
                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                </Text>
                            )}
                            action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => selectTeamToAdd(item)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                        ></List>
                    </SkeletonWrapper>
                </Box>
            </StyledModal>

            <StyledModal
                isOpen={showEntryEditModal}
                setIsOpen={(open) => {
                    setShowEntryEditModal(open);
                    setEntryToEdit({} as OrganizationEntryViewModel);
                    setEntryAliasToEdit([]);
                    setEntryAliasToAdd([]);
                }}
            >
                <Box width={"medium"}>
                    <SkeletonWrapper loading={isEntryLoading}>
                        <Text>Manage Team Members for:</Text>
                        <Box direction="row" gap="small">
                            <TeamRow team={entryToEdit.team}>
                                <Box></Box>
                            </TeamRow>
                        </Box>
                        <Text>Players</Text>

                        <Box gap="small">
                            {entryAliasToEdit.map((participant) => (
                                <AliasRow alias={participant.alias} key={participant.id}>
                                    <Box justify="end" fill>
                                        <Button
                                            color={"status-critical"}
                                            icon={xmark}
                                            label="Remove"
                                            onClick={() => {
                                                setEntryAliasToEdit(entryAliasToEdit.filter((a) => a.id !== participant.id));
                                            }}
                                        ></Button>
                                    </Box>
                                </AliasRow>
                            ))}
                            {entryAliasToAdd.map((alias) => (
                                <AliasRow alias={alias} key={alias.id}>
                                    <Box justify="end" fill>
                                        <Button
                                            color={"status-critical"}
                                            icon={xmark}
                                            label="Remove"
                                            onClick={() => {
                                                setEntryAliasToAdd(entryAliasToAdd.filter((a) => a.id !== alias.id));
                                            }}
                                        ></Button>
                                    </Box>
                                </AliasRow>
                            ))}
                        </Box>
                        <Box direction="row" gap="small">
                            <Button
                                label="Add Player"
                                onClick={() => {
                                    setShowAddAliasModal(true);
                                }}
                            ></Button>
                            <Button
                                primary
                                label="Update"
                                onClick={() =>
                                    updateParticipants().then((response) => {
                                        if (response.success) {
                                            load();
                                            setShowEntryEditModal(false);
                                        }
                                    })
                                }
                            ></Button>
                        </Box>

                        <Text>
                            <pre>{addEntryError}</pre>
                        </Text>
                    </SkeletonWrapper>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showAddAliasModal} setIsOpen={setShowAddAliasModal}>
                <Box width={"medium"}>
                    <OrganizationSearch onSelect={(organization) => setOrganization(organization)}></OrganizationSearch>

                    <FormField label="Search">
                        <TextInput size="xs" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} />
                    </FormField>
                    <List
                        pad="xs"
                        data={userSearchResult}
                        itemKey="id"
                        primaryKey={(member: AliasViewModel) => (
                            <Text size="small" key={member.id}>
                                <Avatar name={member.name} src={member.photoUri} round size="25px"></Avatar> {member.name}
                            </Text>
                        )}
                        action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => addPlayer(item)} disabled={!role} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                    ></List>

                    <Text>
                        <pre>{memberCreateError}</pre>
                    </Text>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveEntryModal} setIsOpen={setShowRemoveEntryModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this team from the invite?</Text>
                    <SkeletonWrapper loading={isEntryLoading}>
                        <Text>
                            <pre>{removeEntryError}</pre>
                        </Text>
                        <Box direction="row" justify="end" pad={"small"} gap="small">
                            <Button onClick={() => setShowRemoveEntryModal(false)} label="Cancel"></Button>
                            <Button primary onClick={() => removeEntryFromTournament()} label="Remove" color="status-critical"></Button>
                        </Box>
                    </SkeletonWrapper>
                </Box>
            </StyledModal>
        </SkeletonWrapper>
    );
};

export default OrganizationInviteView;
